<template>
    <SrListItem>
        <SrListItemAction class="mr-4" :class="{ 'ml-4': bulkSelect }">
            <SrCheckbox v-if="multiple" :input-value="isItemSelected" :indeterminate="false" />
            <SrRadioActive v-else :is-active="isItemSelected"></SrRadioActive>
        </SrListItemAction>
        <SrListItemContent>
            <slot name="item-label" v-bind="{ item }">
                {{ getItemLabel(item) }}
            </slot>
        </SrListItemContent>
    </SrListItem>
</template>

<script>
import SrRadioActive from './SrRadioActive';
import { USER_INPUT_CLASS } from '../../utils/classNames';
import SrCheckbox from '../SrCheckbox/SrCheckbox';
import PropagateChangeEvent from '../../mixins/PropagateChangeEvent';
import SrListItem from '../SrListItem/SrListItem';
import SrListItemAction from '../SrListItemAction/SrListItemAction';
import SrListItemContent from '../SrListItemContent/SrListItemContent';

export default {
    name: 'SrSelectItem',
    components: {
        SrRadioActive,
        SrCheckbox,
        SrListItem,
        SrListItemAction,
        SrListItemContent,
    },
    mixins: [PropagateChangeEvent],
    model: {
        prop: 'value',
        event: 'change',
    },
    props: {
        // eslint-disable-next-line vue/require-default-prop
        value: {
            type: [Object, String, Array, Number],
        },
        item: {
            type: [Object, String],
            required: true,
        },
        bulkSelect: {
            type: Boolean,
            required: true,
        },
        multiple: {
            type: Boolean,
            required: true,
        },
        itemText: {
            type: String,
            default: 'label',
        },
        itemValue: {
            type: String,
            default: 'value',
        },
        returnObject: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        isItemSelected() {
            if (!this.value) {
                return false;
            }
            const itemValue = this.getItemValue(this.item);

            if (this.multiple) {
                if (this.returnObject && this.itemValue) {
                    return this.value.some((item) => item[this.itemValue] === this.item[this.itemValue]);
                }
                return this.value.includes(itemValue);
            }

            if (this.returnObject && this.itemValue) {
                return this.value[this.itemValue] === this.item[this.itemValue];
            }
            return this.value === itemValue;
        },
        /**
         * primitiveValue and primitiveItemValue act as a workaround for a bug in VRadioGroup.
         * Essentially it cannot handle initial values when given objects.
         * Issue here: https://github.com/vuetifyjs/vuetify/issues/10571
         * As soon as this issue is resolved and vuetify is updated,
         * the "value-comparator" property of VRadioGroup can be used instead.
         */
        primitiveValue: {
            get() {
                return this.getPrimitiveValueFrom(this.value);
            },
            set() {
                // don't care about setter
                // The rest is handled by the dropdown recognizing a click event on the item
            },
        },
        primitiveItemValue() {
            return this.getPrimitiveValueFrom(this.item);
        },
        classList() {
            return [USER_INPUT_CLASS];
        },
    },
    methods: {
        getItemLabel(item) {
            if (typeof item === 'string') {
                return item;
            }
            return item[this.itemText];
        },

        getItemValue(item) {
            if (typeof item === 'string') {
                return item;
            }
            if (this.returnObject) {
                return item;
            }
            return item[this.itemValue];
        },

        // tries to return a primitive type.
        // when using objects the prop "itemValue" is required for that
        getPrimitiveValueFrom(item) {
            if (typeof item === 'object') {
                if (this.itemValue) {
                    return item ? item[this.itemValue] : null;
                }
                // eslint-disable-next-line no-console
                console.warn('No itemValue configuration provided. Initial value may be not displayed correctly');
            }
            return item;
        },
    },
};
</script>
